<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { extractServerError } from 'dashboard/helper/appointmentFormErrors';
import ServiceForm from './ServiceForm.vue';

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const searchQuery = ref('');
const showForm = ref(false);
const showDeleteConfirm = ref(false);
const selectedService = ref(null);
// AP-03: backend validation message shown inside the open form on save failure.
const formError = ref('');

const services = computed(
  () => store.getters['appointmentServices/getAppointmentServices']
);
const uiFlags = computed(() => store.getters['appointmentServices/getUIFlags']);

const filteredServices = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return services.value;
  return services.value.filter(s => s.name?.toLowerCase().includes(q));
});

const formatPrice = cents => {
  if (!cents) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
};

onMounted(() => {
  store.dispatch('appointmentServices/get');
});

const openCreateForm = () => {
  selectedService.value = null;
  formError.value = '';
  showForm.value = true;
};

const openEditForm = service => {
  selectedService.value = service;
  formError.value = '';
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedService.value = null;
  formError.value = '';
};

const confirmDelete = service => {
  selectedService.value = service;
  showDeleteConfirm.value = true;
};

const handleFormSubmit = async data => {
  // Backend (services_controller#sync_reminder_templates) já faz replace-all
  // a partir de `service.reminder_templates` no payload. Não chamamos o
  // endpoint /services/:id/reminder_templates separado (estava devolvendo
  // 422 e gerava double-write — bug 2026-05-06).
  formError.value = '';
  try {
    if (selectedService.value?.id) {
      await store.dispatch('appointmentServices/update', {
        id: selectedService.value.id,
        ...data,
      });
      showAlert(t('APPOINTMENTS.SERVICES.UPDATED'));
    } else {
      await store.dispatch('appointmentServices/create', data);
      showAlert(t('APPOINTMENTS.SERVICES.CREATED'));
    }
    // Refresh para trazer reminder_templates persistidos do backend.
    store.dispatch('appointmentServices/get');
    closeForm();
  } catch (error) {
    // AP-03: surface the backend validation message inside the form (which stays
    // open) instead of a generic toast that discarded error.response.
    formError.value = extractServerError(error) || t('APPOINTMENTS.ERROR');
    showAlert(formError.value);
  }
};

const handleDelete = async () => {
  try {
    await store.dispatch(
      'appointmentServices/delete',
      selectedService.value.id
    );
    showAlert(t('APPOINTMENTS.SERVICES.DELETED'));
  } catch (error) {
    showAlert(t('APPOINTMENTS.ERROR'));
  } finally {
    showDeleteConfirm.value = false;
    selectedService.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-n-background">
    <!-- Toolbar: search + CTA -->
    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 px-6 lg:px-8 py-5 border-b border-n-weak/50 bg-n-alpha-1/10"
    >
      <div class="relative flex-1 max-w-md group">
        <span
          class="i-lucide-search absolute left-4 top-1/2 -translate-y-1/2 size-4.5 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
          aria-hidden="true"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          :aria-label="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          class="w-full border border-n-strong rounded-xl !pl-11 !pr-4 !mb-0 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:bg-n-background focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
        />
      </div>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-px active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand whitespace-nowrap"
        @click="openCreateForm"
      >
        <span class="i-lucide-plus size-4.5" aria-hidden="true" />
        {{ t('APPOINTMENTS.SERVICES.ADD') }}
      </button>
    </div>

    <div
      class="flex-1 overflow-y-auto px-6 lg:px-8 py-6 relative custom-scrollbar"
    >
      <!-- Fetch error banner -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
      >
        <div
          v-if="uiFlags.isFetchError"
          class="rounded-xl border border-n-ruby-5 bg-gradient-to-r from-n-ruby-2 to-n-ruby-1 text-n-ruby-11 px-5 py-4 mb-5 text-sm flex items-center gap-3 shadow-sm"
          data-testid="fetch-error-banner"
        >
          <span class="i-lucide-alert-circle size-5" aria-hidden="true" />
          <span class="font-medium">{{ t('APPOINTMENTS.ERROR_FETCH') }}</span>
          <button
            type="button"
            class="ml-auto bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-12 px-4 py-1.5 rounded-lg font-semibold transition-colors"
            @click="store.dispatch('appointmentServices/get')"
          >
            {{ t('APPOINTMENTS.RETRY') }}
          </button>
        </div>
      </Transition>

      <!-- Loading skeleton Premium -->
      <div
        v-if="uiFlags.isFetching"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="i in 4"
          :key="`skeleton-${i}`"
          class="h-36 rounded-[1.25rem] bg-gradient-to-r from-n-alpha-1 to-n-alpha-2 animate-pulse ring-1 ring-black/5"
        >
          <div class="p-5 flex flex-col gap-3 h-full">
            <div class="flex items-center gap-3">
              <div class="size-10 rounded-xl bg-n-background/50" />
              <div class="space-y-2 flex-1">
                <div class="h-4 bg-n-background/50 rounded w-3/4" />
                <div class="h-3 bg-n-background/50 rounded w-1/2" />
              </div>
            </div>
            <div class="mt-auto h-6 bg-n-background/50 rounded-md w-full" />
          </div>
        </div>
      </div>

      <!-- Empty state com ilustração -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
      >
        <div
          v-if="!uiFlags.isFetching && filteredServices.length === 0"
          class="flex flex-col items-center justify-center py-20 text-center rounded-[1.25rem] border border-dashed border-n-strong/50 bg-n-alpha-1/30"
        >
          <div
            class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
          >
            <span
              class="i-lucide-briefcase size-10 text-n-slate-9"
              aria-hidden="true"
            />
          </div>
          <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.SERVICES.EMPTY_STATE') }}
          </h3>
          <p
            class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-5"
          >
            {{
              searchQuery.trim()
                ? t(
                    'APPOINTMENTS.SERVICES.EMPTY_SEARCH_HINT',
                    'Tente outro termo de busca.'
                  )
                : t(
                    'APPOINTMENTS.SERVICES.EMPTY_HINT',
                    'Crie seu primeiro serviço para começar a oferecer atendimentos.'
                  )
            }}
          </p>
          <button
            v-if="!searchQuery.trim()"
            type="button"
            class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand"
            @click="openCreateForm"
          >
            <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            {{ t('APPOINTMENTS.SERVICES.ADD') }}
          </button>
        </div>
      </Transition>

      <!-- Grid Cards Premium -->
      <div
        v-if="!uiFlags.isFetching && filteredServices.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="service in filteredServices"
          :key="service.id"
          class="group relative flex flex-col p-5 rounded-[1.25rem] border border-n-weak bg-n-background hover:bg-n-alpha-1/20 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-n-brand/30 overflow-hidden"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-n-brand/0 to-n-brand/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          />

          <!-- Color indicator e nome -->
          <div class="flex items-start gap-3 mb-4 relative z-10">
            <div
              class="size-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner"
              :style="{
                backgroundColor: (service.color || '#10B981') + '15',
                color: service.color || '#10B981',
                border: `1px solid ${service.color || '#10B981'}30`,
              }"
            >
              <span class="i-lucide-clipboard-list size-6" aria-hidden="true" />
            </div>
            <div class="flex-1 min-w-0 pt-0.5">
              <h3
                class="text-[15px] font-bold text-n-slate-12 leading-tight truncate group-hover:text-n-brand transition-colors"
              >
                {{ service.name }}
              </h3>
              <p
                v-if="service.description"
                class="text-xs font-medium text-n-slate-11 mt-1.5 line-clamp-2"
              >
                {{ service.description }}
              </p>
            </div>
          </div>

          <!-- Metadata: duração + preço -->
          <div
            class="flex items-center gap-3 text-xs font-semibold text-n-slate-12 mt-auto mb-4 bg-n-alpha-1/50 px-3 py-2.5 rounded-xl border border-n-strong/50 relative z-10"
          >
            <span class="inline-flex items-center gap-1.5 flex-1">
              <span
                class="i-lucide-clock size-3.5 text-n-slate-10"
                aria-hidden="true"
              />
              {{
                t('APPOINTMENTS.SERVICES.DURATION_DISPLAY', {
                  n: service.duration_minutes,
                })
              }}
            </span>
            <div class="w-px h-3 bg-n-strong" />
            <span
              class="inline-flex items-center gap-1.5 flex-1 justify-end text-n-teal-11"
            >
              <span class="i-lucide-banknote size-3.5" aria-hidden="true" />
              {{ formatPrice(service.default_price_cents) }}
            </span>
          </div>

          <!-- Actions -->
          <div
            class="flex items-center gap-2 pt-4 border-t border-n-weak/50 opacity-0 group-hover:opacity-100 transition-opacity relative z-10"
          >
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="openEditForm(service)"
            >
              <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.EDIT') }}
            </button>
            <div class="w-px h-4 bg-n-weak/50" />
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-ruby-10 hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
              @click="confirmDelete(service)"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <ServiceForm
      v-if="showForm"
      :service="selectedService"
      :is-submitting="uiFlags.isCreating || uiFlags.isUpdating"
      :server-error="formError"
      @close="closeForm"
      @submit="handleFormSubmit"
    />

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/60 backdrop-blur-sm p-4"
      >
        <div
          class="bg-n-background rounded-[1.25rem] shadow-2xl w-full max-w-sm p-6 ring-1 ring-white/10"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.SERVICES.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t('APPOINTMENTS.SERVICES.DELETE_CONFIRM') }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-5 py-2.5 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="showDeleteConfirm = false"
            >
              {{ t('APPOINTMENTS.SERVICES.FORM.CANCEL') }}
            </button>
            <button
              type="button"
              :disabled="uiFlags.isDeleting"
              class="px-5 py-2.5 text-sm font-semibold text-white bg-n-ruby-9 hover:bg-n-ruby-9/90 rounded-xl disabled:opacity-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-n-ruby-9"
              @click="handleDelete"
            >
              <span
                v-if="uiFlags.isDeleting"
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1"
              />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
